import axios from '../../axios'
import Axios, { AxiosRequestConfig } from 'axios'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { Dataset, CreateDatasetData, IDatasetRulesValidation, TemporaryFile } from './types'
import { Progress } from '@aws-sdk/lib-storage'
import FileUploader from 'app/services/FileUploader'
import { DATASET_STATUS } from 'app/constants'
import { isDatasetInProgress } from 'app/helpers'
import { AlertColor } from '@mui/material/Alert'

export class DatasetsStore {
  loadingList: boolean = false
  list: Dataset[] = []
  entries: {
    [key: string]: Dataset
  } = {}
  totalPages: number = 0
  totalElements: number = 0
  cancelTokenSource = () => {}
  validatedMessageList: { type: AlertColor; message: string[] }[] = []

  constructor() {
    makeObservable(this, {
      loadingList: observable,
      list: observable,
      entries: observable,
      totalPages: observable,
      totalElements: observable,
      validatedMessageList: observable,
      getData: action,
      delete: action,
      setDataset: action,
      reset: action,
      clearList: action,
      removeFromList: action,
      setLoadingList: action,
      validateDelRules: action,
      inProgressDatasets: computed,
    })
  }

  get inProgressDatasets() {
    return this.list.filter(isDatasetInProgress)
  }

  reset = () => {
    this.clearList()
    this.entries = {}
    this.cancelTokenSource = () => {}
  }

  clearList = () => {
    this.loadingList = false
    this.list = []
    this.totalPages = 0
    this.totalElements = 0
    this.validatedMessageList = []
  }

  setLoadingList = (value: boolean) => {
    this.loadingList = value
  }

  getUploadUrl: (type: string) => Promise<string> = async (type) => {
    return await axios('/api/v1/dataset/file/upload/url?type=' + type).then(
      ({ data }) => data
    )
  }

  uploadFile = async (fileRef: any) => {
    const onProgress = (event: Progress) => {
      // @ts-ignore
      fileRef.progress = Math.round((100 * event.loaded) / event.total)
      // Re-render the file list to reflect the new progress
      fileRef.updateView()
    }

    try {
      fileRef.url = await FileUploader.uploadFile(
        fileRef.file,
        this.getUploadUrl,
        onProgress
      )
      fileRef.uploaded = true
      fileRef.uploading = false
      // Re-render the file list to reflect the new changes (uploaded = true)
      fileRef.updateView()
      return fileRef.url
    } catch (error) {
      toast.error('Failed to upload file. Please try again.')
      fileRef.progress = 0
      fileRef.error = true
      throw error
    }
  }

  removeFromList = (id: number) => {
    this.list = this.list.filter((item) => item.id !== id)
  }

  create = async (data: CreateDatasetData) => {
    const datasetFiles = []
    for (const file of data.datasetFiles) {
      // @ts-ignore
      let signedUrl = file.url
      if (!signedUrl) {
        signedUrl = await this.uploadFile(file)
      }
      const splitedUrl = signedUrl.split('?')[0].split('/')
      const fileName = splitedUrl[splitedUrl.length - 1]
      datasetFiles.push(fileName)
    }

    const createDatasetURL = data.isOriginWorkflow ? '/api/v1/dataset/native/create' : '/api/v1/dataset';
    return await axios
      .post(createDatasetURL, {
        ...data,
        datasetFiles,
      })
      .then((response) => {
        toast.success('Created successfully')
        return response
      })
  }

  update = async (id: number, data: object) => {
    return await axios.put('/api/v1/dataset/' + id, data).then((response) => {
      toast.success('Updated successfully')
      return response
    })
  }

  delete = async (id: number) => {
    await axios.delete('/api/v1/dataset/' + id).then(({ data }) => {
      toast.success(
        'Delete is started. You will be notified when the process is completed'
      )
      this.setDataset(data)
    })
  }

  getById = async (id: number) => {
    return await axios(`/api/v1/dataset/${id}?withFeaturesLabels=true`).then(
      ({ data }) => {
        this.setDataset(data)
        return data
      }
    )
  }

  getData = (filters: any = {}) => {
    if (typeof this.cancelTokenSource == 'function') {
      // this.cancelTokenSource()
    }

    if (filters.withIdsFilter && !filters.ids) {
      this.clearList()
      return
    }

    const cancelTokenSource = Axios.CancelToken.source()

    const params: any = {
      ...filters,
      name: filters.searchValue,
      access_filter: filters.sharingStatus,
    }
    delete params.searchValue
    delete params.ownership
    delete params.sharingStatus

    this.setLoadingList(true)

    const config: AxiosRequestConfig = {
      cancelToken: cancelTokenSource.token,
      params,
    }
    axios
      .get(`/api/v1/dataset`, config)
      .then(({ data }) => {
        if (data?.content) {
          runInAction(() => {
            this.list = data.content
            this.totalPages = data.totalPages
            this.totalElements = data.totalElements
          })
        }
      })
      .catch(() => {
        this.clearList()
      })
      .finally(() => {
        this.setLoadingList(false)
      })

    this.cancelTokenSource = cancelTokenSource.cancel
  }

  searchByName = async (name: string) => {
    return await axios
      .get(`/api/v1/dataset`, {
        params: {
          name,
          size: 10,
        },
      })
      .then(({ data }) => data?.content)
  }

  isNameExists = async (name: string) => {
    return await axios(`/api/v1/dataset/name/exists?name=${name}`).then(
      ({ data }) => data
    )
  }

  getDatasetFeatureStats = async (id: number, feature: string) => {
    return await axios(`/api/v1/dataset/${id}/${feature}/stats`).then(
      ({ data }) => data
    )
  }

  getProjectDatasets = async (name: string, otherStatuses?: string) => {
    return await axios
      .get('/api/v1/dataset/projects', {
        params: {
          name,
          otherStatuses,
          size: 1000,
        },
      })
      .then(({ data }) => data.content)
  }

  getDatasetsByIds = async (ids: number[]) => {
    return await axios
      .get('/api/v1/dataset', {
        params: {
          ids: ids.join(','),
          size: 1000,
        },
      })
      .then(({ data }) => data.content as Dataset[])
  }
  refetchListIds = async (ids: number[]) => {
    const exitsIds = ids.filter(
      (id) => this.list.findIndex((item) => item.id === id) !== -1
    )
    const datasets = await this.getDatasetsByIds(exitsIds)
    datasets.forEach((dataset) => {
      const index = this.list.findIndex((item) => item.id === dataset.id)
      runInAction(() => {
        this.list[index] = dataset
      })
    })
  }
  getTrainingDatasets = async (name: string, otherStatuses: [typeof DATASET_STATUS]) => {
    return await axios
      .get('/api/v1/dataset/training', {
        params: {
          name,
          otherStatuses,
          size: 1000,
        },
      })
      .then(({ data }) => data.content)
  }

  getLabels = async (id: number) => {
    return await axios(`/api/v1/dataset/${id}/labels`).then(
      ({ data }) => data.labels
    )
  }

  updateLabels = async (id: number, labels: object) => {
    return await axios
      .put(`/api/v1/dataset/${id}/labels`, { labels })
      .then((response) => {
        toast.success('Updated successfully')
        return response
      })
  }
  setDataset = (dataset: Dataset) => {
    const index = this.list.findIndex((item: Dataset) => item.id === dataset.id)
    if (index !== -1) {
      this.list[index] = dataset
    }
    this.entries[`${dataset.id}`] = dataset
  }

  makeDatasetPrivate = (id: number) => {
    return axios.put('/api/v1/dataset/' + id + '/private/').then(({ data }) => {
      toast.success('The dataset is private now')
      this.setDataset(data)
    })
  }

  makeDatasetPublic = (id: number) => {
    return axios.put('/api/v1/dataset/' + id + '/public/').then(({ data }) => {
      toast.success('The dataset is public now')

      this.setDataset(data)
    })
  }

  cancelDataset = async (id: number) => {
    axios.put('/api/v1/dataset/' + id + '/cancel').then(({ data }) => {
      if (data.status === DATASET_STATUS.CANCELING) {
        toast.success('Started canceling')
        this.setDataset(data)
      } else {
        toast.success('The dataset was cancelled')
        this.removeFromList(id)
      }
    })
  }

  createDatasetFromCSV = async (data: {
    datasetName: string
    csvData: string
  }) => {
    return await axios.post('/api/v1/dataset/import', data).then((response) => {
      toast.success('Created successfully')
      return response
    })
  }

  /**
   * Read dataset's smiles as CSV
   * @param id The dataset's id
   * @param size The number of smiles to export
   */
  getDatasetAsCSV = async (id: number, size: number) => {
    return await axios
      .post(`/api/v1/dataset/${id}/csv` + (!!size ? '?size=' + size : ''), {})
      .then(({ data }) => data)
  }

  downloadDataset = async (
    id: number,
    extra?: { sort?: object; filters?: any }
  ) => {
    const { sort, filters } = extra || {}
    const body = {
      sort,
      filters,
    }
    // @ts-ignore
    await axios
      .post(`/api/v1/dataset/${id}/csv`, body)
      .then(({ data, headers }) => {
        const blob = new Blob([data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        const disposition = headers['content-disposition']
        const matches = /"([^"]*)"/.exec(disposition)
        const filename = matches != null && matches[1] ? matches[1] : 'data.csv'

        link.download = filename
        link.click()
        // window.open(url, '_self')
      })
  }

  getFullDELDatasetUrl = async (id: number) => {
    return await axios(`/api/v1/dataset/${id}/download/url`).then(
      ({ data }) => data as string
    )
  }

  getDocuments = async (id: number) => {
    return await axios('/api/v1/dataset/' + id + '/documents').then(
      ({ data }) => data
    )
  }

  downloadAllDocumentsInZipFile = async (dataseId: number) => {
    return await axios('/api/v1/dataset/' + dataseId + '/documents/zip')
    .then(({ data, headers }) => {
      const link = document.createElement('a')
      link.href = data;
      link.download = 'documents.zip'
      link.click()
    })
  }

  downloadDocument = async (dataseId: number, fileDownloadUri: string) => {
      const link = document.createElement('a')
      link.href = fileDownloadUri
      link.click()
  }

  generateMetaData = async (dataseId: number) => {
    return await axios.post('/api/v1/dataset/' + dataseId + '/meta-data').then(
      ({ data }) => data
    )
  }

  updateDocuments = async (
    id: number,
    body: { deletedFiles: string []; newFiles: TemporaryFile[] }
  ) => {
    return await axios
      .put('/api/v1/dataset/' + id + '/documents', body)
      .then(({ data }) => data)
  }

  validateDelRules = async (rules: string) => {
    return await axios
      .post('/api/v1/dataset/validate', { rules })
      .then(({ data }) => {
        this.validatedMessageList = []
        runInAction(() => {
          if (data.response.full_coverage_guaranteed === 'true') {
            this.validatedMessageList.push({
              type: 'info',
              message: ['The dataset is fully covered by the definitions.'],
            })
          } else if (data.response.full_coverage_guaranteed === 'false') {
            this.validatedMessageList.push({
              type: 'warning',
              message: [
                'Part of the dataset is not covered by the definitions.',
              ],
            })
          }

          if (data.response.no_overlap_guaranteed === 'true') {
            this.validatedMessageList.push({
              type: 'info',
              message: ['There is no overlap between the definitions.'],
            })
          } else if (data.response.no_overlap_guaranteed === 'false') {
            this.validatedMessageList.push({
              type: 'error',
              message: [
                'Some of the samples are shared by classes (label contamination).',
              ],
            })
          }
        })
        if(data.status === "0") {
          this.validatedMessageList.push({
            type: 'error',
            //change it to the error message from AI
            message: [
              'Invalid rules, please check and resubmit! ',
              data.response.error,
            ],
          })
        }
        return data as IDatasetRulesValidation
      })
  }
}

export const datasetsStore = new DatasetsStore()
